import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addNote,
  archiveNote,
  deleteNote,
  getNotes,
  noteReceivers,
  noteState,
  updateNote,
  type Note,
  type NoteRef,
} from '../transport/api'
import { ApiError } from '../transport/http'

/**
 * The note queue for one agent.
 *
 * Three behaviours here are contract, not convenience — each is a hole
 * avengents-server and I closed while designing, and each fails SILENTLY if
 * dropped:
 *
 *  - 409 `stale_reorder`: reorder must carry the complete pending set. A
 *    concurrent add from another device makes our list stale, and applying it
 *    anyway would drop that note with no error. Refetch, retry once with the
 *    fresh order, and if it races again stop and show the true list rather than
 *    looping.
 *  - 404 `unknown_note`: the note was deleted elsewhere. The server refuses to
 *    silently recreate it, so we drop it locally too — otherwise it lingers as a
 *    row that cannot be deleted.
 *  - The endpoint may not exist yet (the backend ships separately). A 404 on the
 *    LIST is not an error state, it is "this build has no notes backend", and
 *    the UI hides the feature instead of showing a permanent failure.
 */

export type NotesUnavailable = 'unavailable' | 'locked' | null

/**
 * A refused write, in words the person can act on.
 *
 * The edge answers 500 `write_failed` when the note store will not take the
 * change (avengents-server-ts/src/wtserver/notes.ts:115) — it used to answer
 * ok:true and lie about it. Nothing was rolled back because nothing needs to be:
 * every handler mutates a freshly loaded array, so a save that did not land
 * leaves no trace (same file, lines 107-114). That is why this says "nothing
 * changed" — the honest instruction is simply to try again. Same shape as
 * friends/friendVerbs.ts:54: known codes in words, otherwise the server's own.
 */
const noteErrText = (e: unknown): string =>
  e instanceof ApiError && e.message === 'write_failed'
    ? 'not saved — the server refused the write, nothing changed'
    : e instanceof Error
      ? e.message
      : 'something went wrong'

/**
 * @param onFail said out loud when a mutation is refused. Optional only so the
 *   hook stays usable without a status channel; WITHOUT it the five mutating
 *   entrances below fail silently, which is the hole the edge's honest 500
 *   exposed — see the onError comment on `edit`.
 */
export function useNotes(ref: NoteRef | null, onFail?: (msg: string) => void) {
  const qc = useQueryClient()
  const key = ['notes', ref?.host ?? null, ref?.session ?? null]

  const q = useQuery({
    queryKey: key,
    queryFn: () => getNotes(ref!.session, ref!.host),
    enabled: !!ref,
    // A missing backend is a fact about the deployment, not a transient fault.
    // A locked vault is not either — App invalidates this on unlock, which is
    // the real dependency; retrying against a locked vault only burns requests.
    retry: (count, err) =>
      !(err instanceof ApiError && (err.status === 404 || err.isVaultLocked)) && count < 2,
  })

  /** Why the feature is not usable, or null when it is. */
  const unavailable: NotesUnavailable = useMemo(() => {
    const e = q.error
    if (!(e instanceof ApiError)) return null
    if (e.status === 404) return 'unavailable'
    if (e.isVaultLocked) return 'locked'
    return null
  }, [q.error])

  const notes = useMemo(() => q.data?.notes ?? [], [q.data])
  const refresh = useCallback(() => void qc.invalidateQueries({ queryKey: key }), [qc, key.join()])

  /** This agent's own receiver row on a note — where state and order live. */
  const mine = useCallback(
    (n: Note) =>
      (n.receivers ?? []).find(
        (r) => r.session === ref?.session && (r.host ?? null) === (ref?.host ?? null),
      ),
    [ref?.session, ref?.host],
  )

  const pending = useMemo(
    () =>
      notes
        .filter((n) => !n.archived && mine(n)?.state === 'pending' && !mine(n)?.detached_ts)
        .sort((a, b) => (mine(a)?.order ?? 0) - (mine(b)?.order ?? 0)),
    [notes, mine],
  )

  const executed = useMemo(
    () =>
      notes
        .filter((n) => !n.archived && mine(n)?.state === 'executed')
        // Newest first: the most recent thing you asked for is the one you are
        // most likely checking on.
        .sort((a, b) => (mine(b)?.executed_ts ?? 0) - (mine(a)?.executed_ts ?? 0)),
    [notes, mine],
  )

  /** A mutation whose 404 means "this note is already gone elsewhere". */
  const settle = useCallback(
    async <T>(run: () => Promise<T>): Promise<T | null> => {
      try {
        return await run()
      } catch (e) {
        if (e instanceof ApiError && e.message === 'unknown_note') {
          refresh() // drop the phantom row rather than leaving it undeletable
          return null
        }
        throw e
      }
    },
    [refresh],
  )

  /**
   * The failure half of `refresh`.
   *
   * `settle` above already swallows 404 unknown_note on purpose (that note is
   * gone elsewhere and the refresh is the whole answer), so anything reaching
   * here is a real refusal the person has to hear about.
   */
  const fail = useCallback((e: unknown) => onFail?.(noteErrText(e)), [onFail])

  const add = useMutation({
    mutationFn: (opts: { text: string; receivers?: NoteRef[]; fileIds?: string[] }) =>
      // Default to THIS agent, but an empty list is legitimate: "save it, I have
      // not decided who it is for yet" is the original request.
      addNote(opts.text, opts.receivers ?? (ref ? [ref] : []), opts.fileIds),
    onSuccess: refresh,
  })

  /*
   * onError on all five entrances below — edit / receivers / setState / archive
   * / remove — each of which had onSuccess and nothing else.
   *
   * That was survivable only while the edge lied with ok:true; now that it
   * answers 500 write_failed (avengents-server-ts/src/wtserver/notes.ts:115) an
   * honest refusal landed as NOTHING on screen, and the row went on showing a
   * change the store never took — worse than the lie, because the person acts
   * on it. `add` above is deliberately left as it was: a refused add shows
   * itself (the draft and its file chips stay put, no row appears in the queue),
   * so it is not one of the silent five.
   */
  const edit = useMutation({
    mutationFn: (o: { id: string; text: string }) => settle(() => updateNote(o.id, o.text)),
    onSuccess: refresh,
    onError: fail,
  })

  const receivers = useMutation({
    mutationFn: (o: {
      id: string
      add?: NoteRef[]
      remove?: NoteRef[]
      detach?: NoteRef[]
    }) => settle(() => noteReceivers(o.id, o)),
    onSuccess: refresh,
    onError: fail,
  })

  const setState = useMutation({
    mutationFn: (o: { id: string; state: 'pending' | 'executed'; ref?: NoteRef }) =>
      settle(() => noteState(o.id, o.ref ?? ref!, o.state)),
    onSuccess: refresh,
    onError: fail,
  })

  const archive = useMutation({
    mutationFn: (o: { id: string; archived: boolean }) =>
      settle(() => archiveNote(o.id, o.archived)),
    onSuccess: refresh,
    onError: fail,
  })

  const remove = useMutation({
    mutationFn: (id: string) => settle(() => deleteNote(id)),
    onSuccess: refresh,
    onError: fail,
  })

  /*
   * `notes`, `mine` and `reorder` were returned here and consumed by nobody. `notes` and
   * `mine` are still used INSIDE this hook (pending/executed derive from them) — the
   * export was the dead part. `reorder` was a whole mutation, 40 lines carrying the 409
   * stale_reorder retry contract, serving a drag-to-reorder UI that NotePanel does not
   * have. All three were invisible to the dead-export guard by construction: it scans
   * `export const|function`, and these are properties of a returned object.
   */
  return {
    loading: q.isPending && !!ref,
    unavailable,
    pending,
    executed,
    refresh,
    add,
    edit,
    receivers,
    setState,
    archive,
    remove,
  }
}
